import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import apiClient from '../../services/api';

// Use env var if set, otherwise use relative URL
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const qualifications = [
  { value: 'SSC', label: 'SSC / 10th' },
  { value: 'Intermediate', label: 'Intermediate / 12th' },
  { value: 'ITI', label: 'ITI / Diploma' },
  { value: 'Degree', label: 'Degree' },
  { value: 'B.Tech', label: 'B.Tech' },
  { value: 'M.Tech', label: 'M.Tech' },
  { value: 'Nursing', label: 'Nursing' },
];

const statusColors = {
  pending: 'warning',
  reviewed: 'info',
  contacted: 'primary',
  completed: 'success',
};

const JobAlertsEnquiries = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [qualificationFilter, setQualificationFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery(
    ['job-alerts-enquiries', statusFilter, qualificationFilter, page],
    async () => {
      const params = {
        page,
        limit: 20,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(qualificationFilter !== 'all' && { qualification: qualificationFilter }),
      };

      const response = await apiClient.get(`/jobalerts/enquiries`, { params });
      return response.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Auto-refresh every 30 seconds
    }
  );

  const { data: stats } = useQuery(
    'job-alerts-enquiries-stats',
    async () => {
      const response = await apiClient.get(`/jobalerts/enquiries/stats`);
      return response.data.data;
    }
  );

  const updateMutation = useMutation(
    async ({ id, status, adminNotes }) => {
      const response = await apiClient.put(
        `/jobalerts/enquiries/${id}`,
        { status, adminNotes }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Enquiry updated successfully');
        queryClient.invalidateQueries('job-alerts-enquiries');
        queryClient.invalidateQueries('job-alerts-enquiries-stats');
        setEditDialogOpen(false);
        setSelectedEnquiry(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update enquiry');
      },
    }
  );

  const handleEdit = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setEditDialogOpen(true);
  };

  const handleView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setViewDialogOpen(true);
  };

  const handleUpdate = (status, adminNotes) => {
    updateMutation.mutate({
      id: selectedEnquiry.id,
      status,
      adminNotes,
    });
  };

  const enquiries = data?.data || [];
  const pagination = data?.pagination || { page: 1, limit: 20, total: 0, totalPages: 1 };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Job Alerts Enquiries (JOBALETSNQRY)
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Enquiries
                </Typography>
                <Typography variant="h4">{stats.total || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Pending
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.byStatus?.pending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Reviewed
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.byStatus?.reviewed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.byStatus?.completed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="contacted">Contacted</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Qualification</InputLabel>
                <Select
                  value={qualificationFilter}
                  label="Qualification"
                  onChange={(e) => {
                    setQualificationFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {qualifications.map((q) => (
                    <MenuItem key={q.value} value={q.value}>
                      {q.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Enquiries Table */}
      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load enquiries. Please refresh.
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : enquiries.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No enquiries found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Village/City</TableCell>
                      <TableCell>Qualification</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiries.map((enquiry) => (
                      <TableRow key={enquiry.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            {enquiry.userName}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" color="action" />
                            {enquiry.userPhone}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon fontSize="small" color="action" />
                            {enquiry.villageCity || '—'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SchoolIcon fontSize="small" color="action" />
                            {enquiry.qualification}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={enquiry.status}
                            color={statusColors[enquiry.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {enquiry.createdAt
                            ? format(new Date(enquiry.createdAt), 'dd MMM yyyy, hh:mm a')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleView(enquiry)}
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Status">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(enquiry)}
                                color="secondary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedEnquiry(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Enquiry Details</DialogTitle>
        <DialogContent>
          {selectedEnquiry && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{selectedEnquiry.userName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone Number
                </Typography>
                <Typography variant="body1">{selectedEnquiry.userPhone}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Village/City
                </Typography>
                <Typography variant="body1">{selectedEnquiry.villageCity || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Qualification
                </Typography>
                <Typography variant="body1">{selectedEnquiry.qualification}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1">{selectedEnquiry.address || '—'}</Typography>
              </Grid>
              {selectedEnquiry.otherInfo && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Other Information
                  </Typography>
                  <Typography variant="body1">{selectedEnquiry.otherInfo}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedEnquiry.status}
                  color={statusColors[selectedEnquiry.status] || 'default'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {selectedEnquiry.createdAt
                    ? format(new Date(selectedEnquiry.createdAt), 'dd MMM yyyy, hh:mm a')
                    : '—'}
                </Typography>
              </Grid>
              {selectedEnquiry.adminNotes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Admin Notes
                  </Typography>
                  <Typography variant="body1">{selectedEnquiry.adminNotes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setViewDialogOpen(false);
            setSelectedEnquiry(null);
          }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setViewDialogOpen(false);
              handleEdit(selectedEnquiry);
            }}
          >
            Edit Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedEnquiry(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Enquiry Status</DialogTitle>
        <DialogContent>
          {selectedEnquiry && (
            <EnquiryEditForm
              enquiry={selectedEnquiry}
              onSubmit={handleUpdate}
              onCancel={() => {
                setEditDialogOpen(false);
                setSelectedEnquiry(null);
              }}
              loading={updateMutation.isLoading}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Edit Form Component
const EnquiryEditForm = ({ enquiry, onSubmit, onCancel, loading }) => {
  const [status, setStatus] = useState(enquiry.status || 'pending');
  const [adminNotes, setAdminNotes] = useState(enquiry.adminNotes || '');

  const handleSubmit = () => {
    onSubmit(status, adminNotes);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="reviewed">Reviewed</MenuItem>
          <MenuItem value="contacted">Contacted</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        label="Admin Notes"
        value={adminNotes}
        onChange={(e) => setAdminNotes(e.target.value)}
        multiline
        rows={4}
        sx={{ mb: 2 }}
        placeholder="Add any notes about this enquiry..."
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Update'}
        </Button>
      </Box>
    </Box>
  );
};

export default JobAlertsEnquiries;






